// Themed confirm dialog (UI polish batch, owner-approved 2026-07-30):
// window.confirm ignores the wv-theme entirely — a native light-grey chrome
// dialog in the middle of the dark-first app — and its copy cannot be styled
// or focus-managed. This is the one replacement; new destructive affordances
// should call wvConfirm rather than window.confirm.
//
// Shape: a promise-based singleton, not a context. Call sites keep the
// window.confirm grammar (`if (!(await wvConfirm('…'))) return;`) so
// converting a site is a one-line change inside an already-async handler.
// ConfirmHost renders the overlay and is mounted ONCE, in index.js beside the
// router — the same "rendered globally, used everywhere" footing as Nav's
// QuickJump, whose backdrop/panel idiom the markup mirrors.
import { useEffect, useState, useRef } from 'react';

let listener = null;

export function wvConfirm(message, { confirmLabel = 'Confirm' } = {}) {
  // Host not mounted (a test render, a detached tree): fall back to the
  // native dialog rather than silently answering for the user.
  if (!listener) return Promise.resolve(window.confirm(message));
  return listener(message, confirmLabel);
}

export function ConfirmHost() {
  const [req, setReq] = useState(null); // { message, confirmLabel, resolve }
  const confirmRef = useRef(null);

  useEffect(() => {
    listener = (message, confirmLabel) =>
      new Promise((resolve) => setReq({ message, confirmLabel, resolve }));
    return () => { listener = null; };
  }, []);

  useEffect(() => {
    if (!req) return undefined;
    // Focus the confirming button: the site asked a yes/no question and Enter
    // should answer it; Escape is the always-available no.
    confirmRef.current?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); answer(false); }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [req]);

  const answer = (ok) => {
    req?.resolve(ok);
    setReq(null);
  };

  if (!req) return null;

  return (
    <div
      onClick={() => answer(false)}
      style={{ position: 'fixed', inset: 0, background: 'color-mix(in srgb, var(--wv-bg) 62%, transparent)', zIndex: 96 }}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-label={req.message}
        onClick={(e) => e.stopPropagation()}
        className="wv-panel"
        style={{
          /* min(): below 448px the panel was flush edge-to-edge (auto margins
             collapse to 0 once maxWidth exceeds the viewport) */
          maxWidth: 'min(420px, calc(100vw - 2 * var(--wv-sp-6)))',
          boxSizing: 'border-box',
          margin: '24vh auto 0',
          padding: 'var(--wv-sp-6)',
          boxShadow: '0 16px 48px var(--wv-shadow)',
        }}
      >
        <div style={{ fontSize: 'var(--wv-fs-md)', marginBottom: 'var(--wv-sp-5)' }}>{req.message}</div>
        <div style={{ display: 'flex', gap: 'var(--wv-sp-3)', justifyContent: 'flex-end' }}>
          <button className="wv-chip" onClick={() => answer(false)}>Cancel</button>
          <button ref={confirmRef} onClick={() => answer(true)} style={{ fontWeight: 700 }}>
            {req.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
